import {
  Button,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Tab,
  Tabs,
  Typography
} from "@material-ui/core";
import { BatteryFull, SignalCellularAltOutlined } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import WifiIcon from "@material-ui/icons/Wifi";
import Hr from "@saleor/components/Hr";
import {
  CompositeDecorator,
  convertFromRaw,
  Editor,
  EditorState
} from "draft-js";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import couponBarcodeImage from "../../../../assets/images/Coupon_Barcode.png";
import useCouponPreviewStyles from "./styles";

export interface CouponPreviewProps {
  data: any;
  open: boolean;
  onClose: () => void;
  mainPromoImg?: any;
  accessPageImg?: any;
  malls: any[];
}
interface TabContentProps {
  activeTab: number;
  data: any;
  mainPromoImg?: File;
  accessPageImg?: any;
  malls?: any[];
}

interface ListProps {
  items: string[];
}

const ImageInfo = () => {
  const classes = useCouponPreviewStyles({});

  return (
    <div className={classes.imageInfo}>
      <InfoOutlinedIcon />
      <Typography>Image may not be the same when published.</Typography>
    </div>
  );
};

const List: React.FC<ListProps> = ({ items }) => (
  <ul>
    {items.map(item => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const CouponPreview: React.FC<CouponPreviewProps> = props => {
  const { data, open, onClose, mainPromoImg, accessPageImg, malls } = props;
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(0);
  const classes = useCouponPreviewStyles({});
  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      className={classes.previewModal}
    >
      <DialogTitle>
        <div className={classes.titleBar}>
          Preview Coupon
          <Button onClick={onClose} id="couponPreviewCloseBtn">
            <CloseIcon
              style={{
                margin: "0"
              }}
            />
          </Button>
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          className={classes.tabContainer}
        >
          <Tab
            id="mainPromoPreviewTab"
            label={intl.formatMessage({ defaultMessage: "Main Promo" })}
            classes={{
              root: classes.tabRoot,
              selected: classes.selectedCouponTabItem
            }}
          />
          <Tab
            id="accessImagePreviewTab"
            label={intl.formatMessage({ defaultMessage: "Access Image" })}
            classes={{
              root: classes.tabRoot,
              selected: classes.selectedCouponTabItem
            }}
          />
        </Tabs>
        <MainPromo
          data={data}
          activeTab={activeTab}
          mainPromoImg={mainPromoImg}
          malls={malls}
        />
        <AccessImage
          data={data}
          activeTab={activeTab}
          accessPageImg={accessPageImg}
        />
      </DialogContent>
    </Dialog>
  );
};

const MainPromo = ({
  activeTab,
  data,
  mainPromoImg,
  malls
}: TabContentProps) => {
  const classes = useCouponPreviewStyles({});
  const formattedDate = new Date(data?.couponEndDate).toLocaleDateString(
    "en-US",
    {
      day: "numeric",
      month: "long",
      year: "numeric"
    }
  );

  const hasMallsOnPreview =
    malls !== undefined && malls.length && data.malls.length > 0;
  const mallList = hasMallsOnPreview
    ? data.malls.map(mallId => {
        const mall = malls.find(mallInfo => mallInfo.id === mallId);
        return mall?.name;
      })
    : ["All malls"];

  const descriptionContentState = convertFromRaw(data?.mainPromoDescription);
  const mechanicsContentState = convertFromRaw(data?.mechanics);

  const mapLinkEntities = (contentBlock, callback, contentState) => {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === "LINK"
      );
    }, callback);
  };

  const linkComponent = props => {
    const { url } = props.contentState.getEntity(props.entityKey).getData();
    return (
      <a href={url} style={{ color: "blue" }}>
        {props.children}
      </a>
    );
  };

  const linkDecorator = new CompositeDecorator([
    {
      component: linkComponent,
      strategy: mapLinkEntities
    }
  ]);

  const descriptionEditorState = EditorState.createWithContent(
    descriptionContentState,
    linkDecorator
  );

  const mechanicsEditorState = EditorState.createWithContent(
    mechanicsContentState,
    linkDecorator
  );
  return (
    <Fade in={activeTab === 0}>
      <div
        className={
          activeTab === 0 ? classes.tabContentActive : classes.tabContent
        }
      >
        <CardContent className={classes.cardContent}>
          <div
            className={classes.mainPromoImg}
            style={{
              backgroundImage: `url(${
                mainPromoImg
                  ? URL.createObjectURL(mainPromoImg)
                  : data.promoImage
              })`
            }}
          >
            <div className={classes.imageContainer}>
              <div className={classes.imageAccesories}>
                <ImageInfo />
                <div className={classes.cellphoneIcons}>
                  <SignalCellularAltOutlined />
                  <WifiIcon />
                  <BatteryFull className={classes.battery} />
                </div>
              </div>
              <div className={classes.paper}></div>
            </div>
          </div>
          <div className={classes.textContainer}>
            <Typography
              variant="h6"
              className={classes.boldTxt}
              style={{
                paddingRight: "30px"
              }}
            >
              {data?.mainPromoTitle}
            </Typography>
            <Typography className={classes.validUntilTxt}>
              Valid until {formattedDate}
            </Typography>
            <Hr className={classes.hr} />
            <Typography className={classes.boldTxtTitle}>
              DESCRIPTION
            </Typography>
            <div className={classes.editorBlockContainer}>
              <Editor
                editorState={descriptionEditorState}
                readOnly={true}
                onChange={undefined}
              />
            </div>
            <Hr className={classes.hr} />
            <Typography className={classes.boldTxtTitle}>
              HOW TO REDEEM
            </Typography>
            <div className={classes.editorBlockContainer}>
              <Editor
                editorState={mechanicsEditorState}
                readOnly={true}
                onChange={undefined}
              />
            </div>
            <Hr className={classes.hr} />
            <Typography className={classes.boldTxtTitle}>
              PARTICIPATING MALLS
            </Typography>
            <Typography className={classes.blockTxtMall}>
              <List items={mallList} />
            </Typography>
          </div>
        </CardContent>
      </div>
    </Fade>
  );
};

const AccessImage = ({ activeTab, data, accessPageImg }: TabContentProps) => {
  const classes = useCouponPreviewStyles({});

  const accessContentState = convertFromRaw(data?.accessPageDescription);

  const mapLinkEntities = (contentBlock, callback, contentState) => {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === "LINK"
      );
    }, callback);
  };

  const linkComponent = props => {
    const { url } = props.contentState.getEntity(props.entityKey).getData();
    return (
      <a href={url} style={{ color: "blue" }}>
        {props.children}
      </a>
    );
  };

  const linkDecorator = new CompositeDecorator([
    {
      component: linkComponent,
      strategy: mapLinkEntities
    }
  ]);

  const accessEditorState = EditorState.createWithContent(
    accessContentState,
    linkDecorator
  );

  return (
    <Fade in={activeTab === 1}>
      <div
        className={
          activeTab === 1 ? classes.tabContentActive : classes.tabContent
        }
      >
        <CardContent
          className={classes.cardContent}
          style={{ padding: "4rem 25px 8rem 25px" }}
        >
          <div className={classes.accessPaper}>
            <img
              src={
                accessPageImg
                  ? URL.createObjectURL(accessPageImg)
                  : data.accessImage
              }
              className={classes.accessImg}
            />
            <ImageInfo />
            <Typography
              variant="h4"
              className={classes.boldTxt}
              style={{
                textAlign: "center"
              }}
            >
              {data?.tagline}
            </Typography>
            <div className={classes.blockTxt}>
              <Editor
                editorState={accessEditorState}
                readOnly={true}
                onChange={undefined}
              />
            </div>
            <Typography className={classes.voucherViewDetails}>
              View Voucher Details
            </Typography>
            <div className={classes.ticketLayer}>
              <div className={classes.circle}></div>
              <Hr className={classes.ticketLine} />
              <div className={classes.circle}></div>
            </div>
            <div className={classes.barcodeSection}>
              <img src={couponBarcodeImage} className={classes.barcodeImage} />
              <Typography>{data?.couponCode}-XXXXX</Typography>
            </div>
          </div>
        </CardContent>
      </div>
    </Fade>
  );
};

CouponPreview.displayName = "CouponPreview";
export default CouponPreview;
